import type {
  AutoTopUpRule,
  PackOfferId,
  TopUpSettings,
} from "@pax/api-contract/payments/v1alpha1";
import { AnimatedCheck, Button, cn, Input, Label, panelCard, panelMotion } from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useState } from "react";
import { api, reachable, requestErrorMessage } from "@/lib/api";
import {
  type AutoTopUpDraft,
  type PackOffer,
  parseThreshold,
  paymentMethodLabel,
  thresholdCredits,
  thresholdFieldError,
} from "../format";
import { topUpSettingsQuery } from "../queries";

const DEFAULT_THRESHOLD = "3";

/** A paused rule needs the player, so the dialog opens on the tab that shows it. */
export function autoTopUpPaused(settings: TopUpSettings | undefined): boolean {
  const state = settings?.autoState;
  return state === "needs_bank" || state === "needs_address" || state === "declined";
}

export type { AutoTopUpDraft } from "../format";

/** What the dialog footer shows while automatic top-up is being turned on or edited. */
export type AutoTopUpFooterMeta = {
  active: true;
  savingEdits: boolean;
  draft: AutoTopUpDraft | null;
  waitingForStripe: boolean;
  disabled: boolean;
};

/**
 * Automatic top-up, under the pack cards: tick the box, set a threshold, and turn the rule on from
 * the dialog footer without buying a pack.
 */
export function AutoTopUpSection({
  settings,
  packs,
  pickedPack,
  disabled,
  onConfirmWithBank,
  onStartSetup,
  onFooterMetaChange,
  setupWaiting,
  setupMissed,
}: {
  settings: TopUpSettings;
  packs: PackOffer[];
  /** The pack selected in the cards above, which is the one a new rule buys. */
  pickedPack: PackOffer | null;
  disabled: boolean;
  /** Opens Checkout for the rule's pack. Called inside the click, so its tab is allowed. */
  onConfirmWithBank: (offerId: PackOfferId) => void;
  /** Opens Stripe setup for automatic charges. Called inside the click, so its tab is allowed. */
  onStartSetup: (offerId: PackOfferId, thresholdRaw: string) => void;
  onFooterMetaChange: (meta: AutoTopUpFooterMeta | null) => void;
  /** The dialog is waiting for a setup tab to turn the rule on. */
  setupWaiting: boolean;
  /** Setup checkout ran but the rule did not turn on in time. */
  setupMissed: boolean;
}) {
  const queryClient = useQueryClient();
  const checkboxId = useId();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AutoTopUpDraft | null>(null);

  useEffect(() => {
    if (settings.autoState === "on") {
      setEditing(false);
      setExpanded(false);
    }
  }, [settings.autoState]);

  const update = useMutation({
    mutationFn: (rule: AutoTopUpRule | null) =>
      reachable(
        rule ? api.payments.autoTopUp.set({ body: rule }) : api.payments.autoTopUp.delete(),
      ),
    onSuccess: updated => {
      queryClient.setQueryData(topUpSettingsQuery.queryKey, updated);
      setEditing(false);
      setExpanded(false);
      setDraft(null);
    },
  });
  const busy = disabled || update.isPending || setupWaiting;
  const failure = update.error;

  const ruleOn = settings.autoState !== "off";
  const rulePack = packs.find(pack => pack.offerId === settings.autoOfferId);
  const packName = rulePack?.name ?? "your pack";
  const threshold = settings.autoThresholdRaw ? thresholdCredits(settings.autoThresholdRaw) : "";
  const methodLabel = settings.paymentMethod
    ? paymentMethodLabel(settings.paymentMethod)
    : "your payment method";
  const resume = () =>
    settings.autoOfferId &&
    settings.autoThresholdRaw &&
    update.mutate({
      autoOfferId: settings.autoOfferId,
      autoThresholdRaw: settings.autoThresholdRaw,
    });

  const setupPack = editing ? (pickedPack ?? rulePack ?? null) : pickedPack;
  const configuringNewRule = settings.autoState === "off" && expanded;
  const footerActive = configuringNewRule || editing;
  const allowsAutomatic = settings.paymentMethod?.allowsAutomatic === true;

  const footerMeta = useMemo((): AutoTopUpFooterMeta | null => {
    if (!footerActive) return null;
    return {
      active: true,
      savingEdits: editing,
      draft,
      waitingForStripe: setupWaiting,
      disabled: busy || draft === null,
    };
  }, [busy, draft, editing, footerActive, setupWaiting]);

  useEffect(() => {
    onFooterMetaChange(footerMeta);
  }, [footerMeta, onFooterMetaChange]);

  let body: ReactNode = null;
  switch (settings.autoState) {
    case "off":
      body = expanded ? (
        <AutoTopUpForm
          pack={setupPack}
          disabled={busy}
          needsSetup={!allowsAutomatic}
          onDraftChange={setDraft}
        />
      ) : null;
      break;
    case "on":
      body = editing ? (
        <AutoTopUpForm
          pack={setupPack}
          disabled={busy}
          initialThreshold={threshold}
          needsSetup={!allowsAutomatic}
          onDraftChange={setDraft}
          onCancel={() => {
            setEditing(false);
            setDraft(null);
          }}
        />
      ) : (
        <Rule
          text={`When you drop below ${threshold} credits, Pax buys ${packName} with ${methodLabel}.`}
          action={
            <Button size="sm" variant="outline" disabled={busy} onClick={() => setEditing(true)}>
              Edit
            </Button>
          }
        />
      );
      break;
    case "charging":
      body = (
        <Rule
          text={`Buying ${packName}…`}
          icon={<RefreshCw aria-hidden className="size-4 shrink-0 animate-spin" />}
        />
      );
      break;
    case "needs_bank":
      body = (
        <Rule
          text={`Your bank wants to confirm ${packName}.`}
          action={
            <Button
              size="sm"
              disabled={busy || !settings.autoOfferId}
              onClick={() => settings.autoOfferId && onConfirmWithBank(settings.autoOfferId)}
            >
              Confirm
            </Button>
          }
        />
      );
      break;
    case "needs_address":
      body = (
        <Rule
          text="Stripe needs your billing address before it can top you up."
          action={
            <Button
              size="sm"
              disabled={busy || !settings.autoOfferId || !settings.autoThresholdRaw}
              onClick={() =>
                settings.autoOfferId &&
                settings.autoThresholdRaw &&
                onStartSetup(settings.autoOfferId, settings.autoThresholdRaw)
              }
            >
              Continue
            </Button>
          }
        />
      );
      break;
    case "declined":
      body = (
        <Rule
          text="Your last payment didn't go through, and any credits it added were removed. Automatic top-up is paused."
          action={
            <Button size="sm" disabled={busy} onClick={resume}>
              Turn back on
            </Button>
          }
        />
      );
      break;
  }

  const checked = ruleOn || expanded;
  const locked = busy || settings.autoState === "charging";

  return (
    <section className={cn(panelCard, "flex flex-col gap-3 p-3")}>
      <label
        htmlFor={checkboxId}
        className={cn(
          "flex cursor-pointer items-start gap-3 rounded-lg has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-foreground/25",
          locked && "cursor-default opacity-60",
          panelMotion,
        )}
      >
        <input
          id={checkboxId}
          type="checkbox"
          className="sr-only"
          checked={checked}
          disabled={locked}
          onChange={() => {
            if (ruleOn) {
              update.mutate(null);
              setEditing(false);
              setDraft(null);
            }
            setExpanded(!checked);
          }}
        />
        <span className="mt-0.5">
          <AnimatedCheck checked={checked} />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium">Top up automatically</span>
          <span className="text-xs text-muted-foreground">
            When your credits drop below a number you pick, Pax buys a pack for you.
          </span>
        </span>
      </label>
      {body}
      {setupMissed && settings.autoState === "off" && (
        <p role="status" className="text-sm text-muted-foreground">
          Automatic top-up didn't turn on. It needs a payment method, authorized on Stripe.
        </p>
      )}
      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {requestErrorMessage(failure)}
        </p>
      )}
    </section>
  );
}

function Rule({ text, icon, action }: { text: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3 pl-8 text-sm">
      {icon}
      <p className="min-w-0 flex-1 text-pretty">{text}</p>
      {action}
    </div>
  );
}

function AutoTopUpForm({
  pack,
  disabled,
  initialThreshold = DEFAULT_THRESHOLD,
  needsSetup,
  onDraftChange,
  onCancel,
}: {
  pack: PackOffer | null;
  disabled: boolean;
  initialThreshold?: string;
  needsSetup: boolean;
  onDraftChange: (draft: AutoTopUpDraft | null) => void;
  onCancel?: () => void;
}) {
  const thresholdId = useId();
  const [typed, setTyped] = useState(initialThreshold);
  const fieldError = thresholdFieldError(typed, pack);
  const thresholdRaw = parseThreshold(typed, pack);

  useEffect(() => {
    if (!pack || !thresholdRaw) {
      onDraftChange(null);
      return;
    }
    onDraftChange({ autoOfferId: pack.offerId, autoThresholdRaw: thresholdRaw });
  }, [onDraftChange, pack, thresholdRaw]);

  return (
    <div className="flex flex-col gap-2 pl-8">
      {pack ? (
        <p className="text-sm leading-relaxed text-pretty">
          When my credits drop below{" "}
          <Input
            id={thresholdId}
            inputMode="decimal"
            value={typed}
            disabled={disabled}
            aria-label="Credit threshold"
            aria-invalid={fieldError !== null}
            onChange={event => setTyped(event.target.value)}
            className="mx-0.5 inline-flex h-8 w-[5.5rem] max-w-full shrink-0 px-2 align-middle text-sm"
          />
          {", buy "}
          {pack.name}.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={thresholdId}>When my credits drop below</Label>
          <Input
            id={thresholdId}
            inputMode="decimal"
            value={typed}
            disabled={disabled}
            aria-invalid={fieldError !== null}
            onChange={event => setTyped(event.target.value)}
          />
          <p className="text-sm text-muted-foreground">Pick the pack to buy above.</p>
        </div>
      )}
      {needsSetup && pack && (
        <p className="text-xs text-muted-foreground">
          Turn on auto top-up opens Stripe so you can approve automatic charges on your payment
          method. Nothing is charged now.
        </p>
      )}
      {onCancel && (
        <div>
          <Button variant="outline" size="sm" disabled={disabled} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
      {fieldError && <p className="text-xs text-destructive">{fieldError}</p>}
    </div>
  );
}
